import React, { useMemo } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import ga4 from 'tracking/src/ga4';
import addUrlHostName from '../../utils/addUrlHostname';
import BrandedLeading from '../BrandedLeading';
import MenuItem from '../../data/MenuItem';
import createTransitionEndHandler from '../../utils/transitions';

type SuperMenuProps = {
  subMenuItem: MenuItem;
  classes?: string;
};

type Menus = {
  primary: MenuItem[];
  secondary: MenuItem[];
};

function SuperMenu({ subMenuItem, classes }: SuperMenuProps) {
  const menus = subMenuItem.children.reduce(
    (acc: Menus, menuItem) => {
      if (menuItem.description) {
        acc.primary.push(menuItem);
      } else {
        acc.secondary.push(menuItem);
      }
      return acc;
    },
    {
      primary: [],
      secondary: [],
    },
  );

  const handleTransitionEnd = useMemo(
    () =>
      createTransitionEndHandler({
        onVisible: () =>
          ga4.assignEvent(process.env.NEXT_PUBLIC_GA4, 'old_topnav_viewed', {
            type: 'navbar_menu_event',
            url: document.URL,
          }),
      }),
    [],
  );

  return (
    <div
      className={twMerge(
        'invisible absolute inset-y-[60%] h-auto w-max max-w-3xl transform opacity-0 duration-300 group-hover:visible group-hover:top-[4.75rem] group-hover:opacity-100',
        '[&.group-hover:visible]:[--is-visible:1]',
        classes,
      )}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="relative flex gap-10 bg-white p-10 text-left drop-shadow-lg">
        {menus.primary.length > 0 && (
          <div className="flex flex-col gap-4">
            {menus.primary.map((menu) => (
              <div className="flex flex-col" key={menu.id}>
                <a
                  href={`${addUrlHostName(menu.path)}?nav=old`}
                  className="text-site-neutrals-800 site-paragraph-xl font-bold hover:underline"
                >
                  <BrandedLeading as="span" className="">
                    {menu.label}
                  </BrandedLeading>
                </a>
                <span className="site-paragraph-s text-site-neutrals-600">{menu.description}</span>
              </div>
            ))}
          </div>
        )}
        {menus.secondary.length > 0 && (
          <div className="flex flex-col gap-4 border-l-2">
            {menus.secondary.map((menu) => (
              <div className="flex flex-col pl-9" key={menu.id}>
                <a
                  href={`${addUrlHostName(menu.path)}?nav=old`}
                  className="site-paragraph-m text-site-neutrals-700 hover:underline"
                >
                  {menu.label}
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SuperMenu;
